import { useState } from "react";
import {
  Box,
  Text,
  Group,
  Badge,
  ActionIcon,
  Select,
  TextInput,
  Anchor,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronRight,
  IconSearch,
  IconX,
  IconFileText,
  IconSettings,
  IconTrash,
  IconCheck,
  IconAlertCircle,
  IconMail,
} from "@tabler/icons-react";
import { DataTable } from "../DataTable";
import dayjs from "dayjs";
import { useDebouncedValue } from "@mantine/hooks";
import { useGetHistoryLogs } from "../../api/registrations";
import { useSearchParams } from "react-router-dom";
import { ChangesList } from "./ChangesList";

const ACTION_CONFIG = {
  created: {
    label: "Created",
    color: "blue",
    icon: IconFileText,
  },
  updated: {
    label: "Updated",
    color: "cyan",
    icon: IconSettings,
  },
  deleted: {
    label: "Deleted",
    color: "red",
    icon: IconTrash,
  },
  approved: {
    label: "Approved",
    color: "green",
    icon: IconCheck,
  },
  rejected: {
    label: "Rejected",
    color: "red",
    icon: IconAlertCircle,
  },
  invoiced: {
    label: "Invoiced",
    color: "orange",
    icon: IconFileText,
  },
  email_sent: {
    label: "Email Sent",
    color: "violet",
    icon: IconMail,
  },
  settings_updated: {
    label: "Settings Updated",
    color: "gray",
    icon: IconSettings,
  },
};

const ACTION_OPTIONS = Object.entries(ACTION_CONFIG).map(
  ([value, { label }]) => ({
    value,
    label,
  })
);

function History({ eventId, onRegistrationClick }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedRows, setExpandedRows] = useState([]);

  const page = parseInt(searchParams.get("page") || "1", 10);
  const limit = parseInt(searchParams.get("limit") || "10", 10);

  const filter = (() => {
    try {
      const filterParams = searchParams.get("filter");
      return filterParams ? JSON.parse(filterParams) : null;
    } catch (e) {
      return null;
    }
  })();

  const [debouncedFilter] = useDebouncedValue(filter, 200);

  const queryParams = {
    page,
    limit,
  };

  if (debouncedFilter?.action) {
    queryParams.action = debouncedFilter.action;
  }

  if (debouncedFilter?.user) {
    queryParams.user = debouncedFilter.user;
  }

  const { data, isPending } = useGetHistoryLogs(eventId, queryParams);

  const handlePageChange = (newPage) => {
    setSearchParams((params) => {
      params.set("page", newPage.toString());
      return params;
    });
  };

  const handleFilterChange = (newFilter) => {
    setSearchParams((params) => {
      params.set("page", "1");
      if (!newFilter) {
        params.delete("filter");
      } else {
        params.set("filter", JSON.stringify(newFilter));
      }
      return params;
    });
  };

  return (
    <DataTable
      mt="md"
      records={data?.data}
      pagination={{ page, limit }}
      onPageChange={handlePageChange}
      page={page}
      recordsPerPage={limit}
      totalRecords={data?.pagination?.total}
      borderRadius="md"
      verticalSpacing="md"
      rowExpansion={{
        allowMultiple: true,
        expanded: {
          recordIds: expandedRows,
        },
        content: ({ record }) => (
          <Box p="md" bg="gray.0">
            <Text fw={600} size="sm" mb="xs">
              Change Details:
            </Text>
            <ChangesList changes={record.changes} />
          </Box>
        ),
      }}
      columns={[
        {
          accessor: "action",
          width: 220,
          render: ({ action, id }) => {
            const {
              label,
              color,
              icon: Icon,
            } = ACTION_CONFIG[action] ?? {
              label: action,
              color: "gray",
              icon: IconFileText,
            };

            return (
              <Group gap="xs" wrap="nowrap">
                <ActionIcon
                  variant="light"
                  color="gray"
                  size="sm"
                  onClick={() => {
                    setExpandedRows((rows) =>
                      rows.includes(id)
                        ? rows.filter((row) => row !== id)
                        : [...rows, id]
                    );
                  }}
                >
                  {expandedRows.includes(id) ? (
                    <IconChevronDown size={14} />
                  ) : (
                    <IconChevronRight size={14} />
                  )}
                </ActionIcon>
                <Badge
                  leftSection={<Icon size={16} />}
                  color={color}
                  variant="light"
                >
                  {label}
                </Badge>
              </Group>
            );
          },
          filter: (
            <Select
              label="Action"
              description="Filter by action type"
              placeholder="All actions"
              data={ACTION_OPTIONS}
              value={filter?.action}
              onChange={(value) =>
                handleFilterChange(value ? { ...filter, action: value } : null)
              }
              clearable
            />
          ),
          filtering: filter?.action,
        },
        {
          accessor: "userName",
          title: "User",
          render: ({ userName, userEmail }) => (
            <Group gap="xs" wrap="nowrap">
              <Text size="sm">{userName || userEmail || "System"}</Text>
            </Group>
          ),
          filter: (
            <TextInput
              label="User"
              description="Filter by user name or email"
              placeholder="Search user..."
              leftSection={<IconSearch size={16} />}
              rightSection={
                filter?.user && (
                  <ActionIcon
                    size="sm"
                    variant="transparent"
                    c="dimmed"
                    onClick={() => handleFilterChange(null)}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                )
              }
              value={filter?.user || ""}
              onChange={(e) => {
                if (e.currentTarget.value === "") {
                  handleFilterChange(null);
                  return;
                }
                handleFilterChange({ user: e.currentTarget.value });
              }}
            />
          ),
          filtering: filter?.user,
        },
        {
          accessor: "registrationId",
          title: "Registration",
          render: ({ registrationId }) =>
            registrationId ? (
              <Anchor
                component="button"
                type="button"
                onClick={() => onRegistrationClick?.(registrationId)}
                size="sm"
                fw={500}
              >
                #{registrationId}
              </Anchor>
            ) : (
              "—"
            ),
        },
        {
          accessor: "ticketId",
          title: "Ticket",
          render: ({ ticketId }) => (ticketId ? `#${ticketId}` : "—"),
        },
        {
          accessor: "updatedAt",
          title: "Timestamp",
          render: ({ updatedAt }) => (
            <Text size="sm">
              {dayjs(updatedAt).format("YYYY-MM-DD HH:mm:ss")}
            </Text>
          ),
        },
        {
          accessor: "changes",
          title: "Changes",
          render: ({ changes }) => {
            const changesCount = changes ? Object.keys(changes).length : 0;
            return (
              <Text size="sm" c="dimmed">
                {changesCount} {changesCount === 1 ? "field" : "fields"} changed
              </Text>
            );
          },
        },
      ]}
      fetching={isPending}
    />
  );
}

export default History;
